'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiLink, {
  type LinkTypeMap as MuiLinkTypeMap,
} from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';

import type { ExcludedMuiTypographyProps } from '../typography/Typography';

type ExcludedMuiProps = ExcludedMuiTypographyProps;

type CustomProps = {
  color?: 'forceWhite' | 'inherit' | 'primary';
  component?: ElementType;
};

type LinkTypeMap<
  P = CustomProps,
  D extends ElementType = MuiLinkTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiLinkTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `Link` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-link-migration--link-migration-guide Migration guide}
 */
export type LinkProps<
  D extends ElementType = MuiLinkTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiLinkTypeMap<P, D>, D>;

const LinkComponent = (
  { color, ...rest }: LinkProps,
  ref: Ref<HTMLAnchorElement>
) => {
  const theme = useTheme();
  const resolvedColor =
    color === 'forceWhite' ? theme.palette.forceWhite.main : color;

  return <MuiLink {...rest} ref={ref} color={resolvedColor} />;
};

LinkComponent.displayName = 'Link';

/**
 * @deprecated Please use `Link` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-link-migration--link-migration-guide Migration guide}
 */
export const Link = forwardRef(
  LinkComponent
) as OverridableComponent<LinkTypeMap>;
